// `ver_productos`: el agente mira las fotos del catálogo ANTES de escribir prompts —
// ADR-2026-09-08-el-chat-trabaja-en-lote.
//
// No tiene tarjeta: el `tool_result` lleva bloques de imagen (URL firmada de la miniatura
// del catálogo) más una línea de texto por producto. El motor manda el array tal cual como
// contenido del resultado (Anthropic lo acepta; para Gemini el backend traduce las
// imágenes a partes inline). Puro para poder probarlo.

import type { MockProductV2 } from '../mockData';
import type { FotoDeProducto } from '../productsApi';

/** Tope de fotos por llamada: cada una son tokens de imagen en ESTE turno y en todos los
 *  siguientes del historial. */
export const MAX_PRODUCTOS_A_VER = 12;

/** Hasta esta cantidad de productos se muestran TODAS sus vistas (frontal, trasera…);
 *  con más, sólo la portada y la lista de vistas en texto. Con 4 productos × 4 vistas
 *  son 16 imágenes en el turno; con 12 serían 48, que se arrastran en todo el historial
 *  (ADR-2026-09-13-el-agente-sabe-que-vistas-tiene-cada-producto). */
export const PRODUCTOS_CON_TODAS_LAS_VISTAS = 4;

const nombreDeVista = (v: string | null): string => v ?? 'sin rótulo';

export type BloqueContenido =
  | { type: 'text'; text: string }
  | { type: 'image'; source: { type: 'url'; url: string } };

/** Los ids que pidió el modelo, saneados y acotados. */
export function idsPedidos(entrada: unknown): string[] {
  const ids = (entrada as { product_ids?: unknown } | null)?.product_ids;
  if (!Array.isArray(ids)) return [];
  const vistos = new Set<string>();
  const out: string[] = [];
  for (const raw of ids) {
    const id = typeof raw === 'string' || typeof raw === 'number' ? String(raw).trim() : '';
    if (!id || vistos.has(id)) continue;
    vistos.add(id);
    out.push(id);
    if (out.length >= MAX_PRODUCTOS_A_VER) break;
  }
  return out;
}

/**
 * Los bloques del `tool_result`. Un producto sin foto se nombra igual (sin imagen); uno
 * que no está en el catálogo se lista como no encontrado, así el modelo no lo inventa.
 */
export function bloquesVerProductos(
  ids: string[],
  productos: Pick<MockProductV2, 'id' | 'name' | 'imageUrl'>[],
  /** Las fotos de cada producto con su vista. Sin esto (la consulta falló) se muestra la
   *  portada sola, como siempre. */
  vistas?: Map<string, FotoDeProducto[]>,
): BloqueContenido[] {
  if (ids.length === 0) {
    return [{ type: 'text', text: 'No pasaste ningún product_id. Pasá los ids del catálogo.' }];
  }
  const porId = new Map(productos.map((p) => [String(p.id), p]));
  const bloques: BloqueContenido[] = [];
  const faltantes: string[] = [];
  const todasLasVistas = ids.length <= PRODUCTOS_CON_TODAS_LAS_VISTAS;
  let n = 0;
  for (const id of ids) {
    const p = porId.get(id);
    if (!p) {
      faltantes.push(id);
      continue;
    }
    n += 1;
    const fotos = vistas?.get(String(p.id)) ?? [];
    if (fotos.length > 0) {
      // Qué lados existen, SIEMPRE en texto: es lo que el agente necesita para planear
      // poses aunque no vea cada foto.
      const lista = fotos.map((f) => nombreDeVista(f.vista)).join(', ');
      bloques.push({
        type: 'text',
        text: `#${n} product_id=${p.id} · ${p.name} · ${fotos.length} foto(s): ${lista}${todasLasVistas ? ':' : ' (te muestro la portada)'}`,
      });
      if (todasLasVistas) {
        fotos.forEach((f, k) => {
          if (fotos.length > 1) bloques.push({ type: 'text', text: `#${n}.${k + 1} vista ${nombreDeVista(f.vista)}:` });
          bloques.push({ type: 'image', source: { type: 'url', url: f.url } });
        });
      } else if (p.imageUrl) {
        bloques.push({ type: 'image', source: { type: 'url', url: p.imageUrl } });
      }
      continue;
    }
    bloques.push({
      type: 'text',
      text: `#${n} product_id=${p.id} · ${p.name}${p.imageUrl ? ':' : ' (sin foto cargada)'}`,
    });
    if (p.imageUrl) bloques.push({ type: 'image', source: { type: 'url', url: p.imageUrl } });
  }
  if (faltantes.length > 0) {
    bloques.push({
      type: 'text',
      text: `No encontré en el catálogo: ${faltantes.join(', ')}.`,
    });
  }
  if (n > 0) {
    bloques.unshift({
      type: 'text',
      text: `Estas son las fotos de ${n} producto(s) del catálogo, en orden. Mirálas y usá lo que ves (si es prenda o accesorio, cómo se usa, qué escena y qué pose le van) para planear y escribir los prompts; el producto en sí NO lo describas por piezas en el prompt: viaja como referencia y el motor lo copia de la foto (REGLA 2c). Cada producto dice QUÉ VISTAS tiene: el motor sólo conoce esos lados de la prenda — si falta la trasera, no pidas poses de espaldas (la inventaría) o pedile al usuario esa foto.`,
    });
  }
  return bloques;
}
